import React, { useState, useMemo } from 'react';
import { Typography, Paper, Box, Grid, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Slider } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { simulateGrowth } from '../../utils/growthSimulator';

const SimulationTab = ({ forestStands, activities }) => {
    const [selectedStandId, setSelectedStandId] = useState('all');
    const [forecastYears, setForecastYears] = useState(80);
    const [includeActivities, setIncludeActivities] = useState(true);

    const simulationData = useMemo(() => {
        if (!forestStands || forestStands.features.length === 0) return [];

        const activeStands = selectedStandId === 'all' 
            ? forestStands.features 
            : forestStands.features.filter(f => f.id === selectedStandId);

        if (activeStands.length === 0) return [];

        // For "All Stands", we aggregate the simulation results
        if (selectedStandId === 'all') {
            const allRuns = activeStands.map(stand => {
                const species = (stand.properties.species && stand.properties.species.length > 0) 
                    ? stand.properties.species 
                    : [{ species: 'Tall', percentage: 100 }];
                const standActivities = includeActivities ? activities.filter(a => a.stand_id === stand.id) : [];
                return {
                    run: simulateGrowth(stand.properties, species, standActivities, forecastYears),
                    area: parseFloat(stand.properties.area_hectares) || parseFloat(stand.properties.area) || 0
                };
            });

            const totalArea = allRuns.reduce((sum, item) => sum + item.area, 0);

            // Aggregate by year
            const aggregated = [];
            for (let t = 0; t <= forecastYears; t++) {
                let volTotal = 0;
                let stemsWeighted = 0;
                let heightWeighted = 0;
                let ageWeighted = 0;
                let baWeighted = 0;
                let incrementWeighted = 0;
                let incrementTotal = 0;
                const year = allRuns[0].run[t].year;
                
                allRuns.forEach(item => {
                    const runPoint = item.run[t];
                    const area = item.area;
                    volTotal += (runPoint.total_volume || 0);
                    stemsWeighted += ((runPoint.stems_per_ha || 0) * area);
                    heightWeighted += ((runPoint.height_m || 0) * area);
                    ageWeighted += ((runPoint.age || 0) * area);
                    baWeighted += ((runPoint.basal_area_m2_ha || 0) * area);
                    incrementWeighted += ((runPoint.annual_increment_m3sk || 0) * area);
                    incrementTotal += ((runPoint.annual_increment_m3sk || 0) * area); // Total m3sk for the farm
                });

                aggregated.push({ 
                    year, 
                    total_volume: Math.round(volTotal) || 0,
                    volume_ha: totalArea > 0 ? Math.round((volTotal / totalArea) * 10) / 10 : 0,
                    stems_per_ha: totalArea > 0 ? Math.round(stemsWeighted / totalArea) : 0,
                    height_m: totalArea > 0 ? Math.round((heightWeighted / totalArea) * 10) / 10 : 0,
                    age: totalArea > 0 ? Math.round(ageWeighted / totalArea) : 0,
                    basal_area_m2_ha: totalArea > 0 ? Math.round((baWeighted / totalArea) * 10) / 10 : 0,
                    annual_increment_m3sk: totalArea > 0 ? Math.round((incrementWeighted / totalArea) * 10) / 10 : 0,
                    total_annual_increment: Math.round(incrementTotal) || 0
                });
            }
            return aggregated;
        } else {
            // Single stand simulation
            const stand = activeStands[0];
            const species = (stand.properties.species && stand.properties.species.length > 0) 
                ? stand.properties.species 
                : [{ species: 'Tall', percentage: 100 }];
            const standActivities = includeActivities ? activities.filter(a => a.stand_id === stand.id) : [];
            return simulateGrowth(stand.properties, species, standActivities, forecastYears);
        }
    }, [selectedStandId, forestStands, activities, forecastYears, includeActivities]);

    const activeStand = selectedStandId !== 'all' ? forestStands.features.find(f => f.id === selectedStandId) : null;

    return (
        <Paper sx={{ p: 2, minHeight: '400px' }}>
            <Typography variant="h6" gutterBottom>Growth Simulation</Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Target Stand</InputLabel>
                        <Select
                            value={selectedStandId}
                            label="Target Stand"
                            onChange={(e) => setSelectedStandId(e.target.value)}
                        >
                            <MenuItem value="all">All Stands (Farm Total)</MenuItem>
                            {forestStands.features.map(f => (
                                <MenuItem key={f.id} value={f.id}>Stand {f.properties.stand_number}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box sx={{ px: 2 }}>
                        <Typography variant="caption">Forecast Horizon: {forecastYears} years</Typography>
                        <Slider
                            value={forecastYears}
                            min={10}
                            max={150}
                            step={10}
                            onChange={(e, val) => setForecastYears(val)}
                            valueLabelDisplay="auto"
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControlLabel
                        control={<Checkbox checked={includeActivities} onChange={(e) => setIncludeActivities(e.target.checked)} />}
                        label="Include Planned Activities"
                    />
                </Grid>
            </Grid>

            <Box sx={{ width: '100%', height: 450 }}>
                {simulationData.length > 0 ? (
                    <ResponsiveContainer>
                        <LineChart data={simulationData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="year" />
                            <YAxis 
                                yAxisId="left"
                                label={{ value: 'm3sk/ha, BA, H, Age', angle: -90, position: 'insideLeft' }} 
                            />
                            <YAxis 
                                yAxisId="right" 
                                orientation="right"
                                label={{ value: 'Total m3sk, Density, Growth', angle: 90, position: 'insideRight' }} 
                            />
                            <Tooltip />
                            <Legend />
                            {/* Primary Metrics */}
                            <Line yAxisId="left" type="monotone" dataKey="volume_ha" name="Volume (m3sk/ha)" stroke="#2e7d32" strokeWidth={3} dot={false} />
                            <Line yAxisId="right" type="monotone" dataKey="total_volume" name="Total Volume (m3sk)" stroke="#1b5e20" strokeWidth={2} strokeDasharray="3 3" dot={false} />
                            
                            {/* Additional Metrics */}
                            <Line yAxisId="right" type="monotone" dataKey="stems_per_ha" name="Density (stems/ha)" stroke="#0288d1" dot={false} />
                            <Line yAxisId="left" type="monotone" dataKey="height_m" name="Height (m)" stroke="#ed6c02" dot={false} />
                            <Line yAxisId="left" type="monotone" dataKey="age" name="Age" stroke="#9c27b0" dot={false} />
                            <Line yAxisId="left" type="monotone" dataKey="basal_area_m2_ha" name="Basal Area (m2/ha)" stroke="#ff9800" dot={false} />
                            <Line yAxisId="left" type="monotone" dataKey="annual_increment_m3sk" name="Growth (m3sk/ha/yr)" stroke="#f44336" dot={false} strokeWidth={2} />
                            <Line yAxisId="right" type="monotone" dataKey="total_annual_increment" name="Total Farm Growth (m3sk/yr)" stroke="#d32f2f" dot={false} strokeDasharray="3 3" />

                            {/* Species breakdown (only for single stand) */}
                            {selectedStandId !== 'all' && activeStand?.properties?.species?.map((s, idx) => (
                                <Line 
                                    key={s.species}
                                    yAxisId="left"
                                    type="monotone" 
                                    dataKey={`species_data[${idx}].volume_ha`} 
                                    name={s.species === 'Bjork' ? `${s.species} (m3sk/ha)` : `${s.species} (m3sk/ha)`} 
                                    stroke={['#4caf50', '#81c784', '#a5d6a7'][idx % 3]} 
                                    strokeDasharray="5 5"
                                    dot={false}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <Typography color="textSecondary">Create a stand to see growth simulation</Typography>
                    </Box>
                )}
            </Box>

            {activeStand && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2">Stand Summary: {activeStand.properties.area_hectares} ha</Typography>
                    <Typography variant="body2">Current: {activeStand.properties.volume_m3sk_ha} m3sk/ha at age {activeStand.properties.age}</Typography>
                    <Typography variant="body2" color="primary">
                        Projected in 10 years: {simulationData[10]?.volume_ha || 'N/A'} m3sk/ha
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default SimulationTab;
